/**
 * SeasonService — singleton that reads season.json and exposes helpers for
 * computing the current season level from accumulated XP.
 *
 * Layer: Service (Layer 4 — Persistence / helper). No Phaser imports.
 */

import seasonData from '../data/season.json'

export type SeasonRewardType = 'gold' | 'gems' | 'pack' | 'title' | 'cardXp'

export interface SeasonReward {
  type: SeasonRewardType
  value: number
}

export interface SeasonLevel {
  level: number
  freeReward: SeasonReward
  premiumReward: SeasonReward
}

export interface SeasonConfig {
  id: string
  name: string
  startDate: string
  endDate: string
  xpPerLevel: number
  levels: SeasonLevel[]
}

// Season.json is imported as unknown — validate the shape we care about.
function loadConfig(): SeasonConfig {
  const raw = seasonData as unknown as SeasonConfig
  return raw
}

export class SeasonService {
  private static _instance: SeasonService | null = null
  private readonly _config: SeasonConfig

  private constructor() {
    this._config = loadConfig()
  }

  static getInstance(): SeasonService {
    if (!SeasonService._instance) {
      SeasonService._instance = new SeasonService()
    }
    return SeasonService._instance
  }

  get config(): SeasonConfig {
    return this._config
  }

  /** True if the current UTC date is within the season's start/end window. */
  isActive(): boolean {
    const now = Date.now()
    const start = new Date(this._config.startDate).getTime()
    const end = new Date(this._config.endDate).getTime()
    return now >= start && now < end
  }

  /**
   * Season pass level (1–50) computed from accumulated season XP.
   * Level 1 starts at 0 XP. Level n requires (n-1) * xpPerLevel XP.
   */
  getLevelFromXp(seasonXp: number): number {
    const level = Math.floor(seasonXp / this._config.xpPerLevel) + 1
    return Math.min(level, this._config.levels.length)
  }

  /** XP required to reach the NEXT level (returns 0 at max level). */
  getXpToNextLevel(seasonXp: number): number {
    const level = this.getLevelFromXp(seasonXp)
    if (level >= this._config.levels.length) return 0
    const nextLevelXp = level * this._config.xpPerLevel
    return nextLevelXp - seasonXp
  }

  /** XP within the current level (0 to xpPerLevel-1). */
  getXpInCurrentLevel(seasonXp: number): number {
    return seasonXp % this._config.xpPerLevel
  }

  /** Returns the SeasonLevel definition for a given level number (1-based). */
  getLevelDef(level: number): SeasonLevel | undefined {
    return this._config.levels.find((l) => l.level === level)
  }

  /** ISO date string for the season end. */
  getEndDate(): string {
    return this._config.endDate
  }

  /** Total number of levels in the season (50). */
  get maxLevel(): number {
    return this._config.levels.length
  }

  /** True if the current date is past the season's endDate. */
  isSeasonOver(): boolean {
    return new Date() >= new Date(this._config.endDate)
  }

  /** Days remaining until season end (0 if over). */
  daysRemaining(): number {
    const ms = new Date(this._config.endDate).getTime() - Date.now()
    return Math.max(0, Math.ceil(ms / 86_400_000))
  }

  /** League-based end-of-season rewards (gold + gems + title). */
  getSeasonEndRewards(league: string): { gold: number; gems: number; title: string } {
    const rewards: Record<string, { gold: number; gems: number; title: string }> = {
      diamond:  { gold: 5000, gems: 500, title: 'Arkaner Meister' },
      platinum: { gold: 3000, gems: 300, title: 'Platin-Krieger' },
      gold:     { gold: 2000, gems: 150, title: 'Goldener Verteidiger' },
      silver:   { gold: 1000, gems:  75, title: 'Silberner Wächter' },
      bronze:   { gold:  500, gems:  25, title: 'Bronzene Klinge' },
    }
    return rewards[league] ?? rewards['bronze']
  }

  /** Soft ELO reset value: keeps some rank info. New ELO = floor(currentElo * 0.5) + 500. */
  softResetElo(currentElo: number): number {
    return Math.floor(currentElo * 0.5) + 500
  }
}
